import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  ArgumentAxis,
  Chart,
  CommonSeriesSettings,
  Crosshair,
  Export,
  Label,
  Legend,
  Margin,
  Point,
  ScrollBar,
  Series,
  TickInterval,
  Tooltip,
  ValueAxis,
  VisualRange,
  ZoomAndPan
} from 'devextreme-react/chart';
import { Button, DatePicker, Popconfirm, Tabs } from 'antd';
import '../../assets/css/history.scss';
import Logo from '../../assets/img/logo.svg';
import moment from 'moment';
import { ExportToCsv } from 'export-to-csv';
import { useDispatch, useSelector } from 'react-redux';
import commonMethod from '../../lib/commonMethod';
import cookie from 'react-cookies';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

function RefTempHumidityHistory({
  deviceId,
  deviceName,
  deviceType,
  history: routeHistory
}) {
  const dispatch = useDispatch();
  const accessToken = useMemo(() => cookie.load('accessToken'), []);
  const [visualRange, setVisualRange] = useState({
    startValue: undefined,
    endValue: undefined
  });
  const { history, startAt, endAt, loading } = useSelector(
    state => state.history
  );
  const { isRefresh, navigationWidth } = useSelector(state => state.hejhome);
  const chart = useRef(null);

  const deviceCodeMap = useMemo(
    () => ({
      SensorRefTh: { va_humidity: 'humi', va_temperature: 'temp' },
      SensorRefTh2: { va_humidity: 'humi', va_temperature: 'temp' }
    }),
    []
  );

  useEffect(() => {
    if (!chart.current) {
      return;
    }
    // navigation open,close가 transform 0.2초 -> width 변경을 0.2초 전에는 인지하지 못해 0.21초 후 chart를 render 시키는 setTimeout 추가
    setTimeout(() => {
      if (chart?.current?.instance) {
        chart.current.instance.render();
      }
    }, 210);
  }, [navigationWidth]);

  useEffect(() => {
    const startAt = moment().subtract(1, 'week').add(1, 'day').startOf('day');
    const endAt = moment().endOf('day');
    dispatch({ type: 'setLoading', loading: true });
    dispatch({ type: 'setDate', startAt, endAt });
  }, [dispatch]);

  const tempHumPerTime = perTime => {
    const codes = deviceCodeMap[deviceType];
    let firstTime;
    const tempHumPerTimeData = (history || []).reduce((prev, data) => {
      const currentSECOND = Math.floor(data.t / perTime) * perTime;
      if (!firstTime) firstTime = currentSECOND;
      prev[currentSECOND] = prev[currentSECOND] || { t: currentSECOND };
      if (prev[currentSECOND][codes[data.code]]) {
        prev[currentSECOND][codes[data.code]] =
          prev[currentSECOND][codes[data.code]] + Math.floor(data.value / 10);
        if (codes[data.code] === 'humi') prev[currentSECOND].humiCount++;
        else prev[currentSECOND].tempCount++;
      } else {
        prev[currentSECOND][codes[data.code]] = Math.floor(data.value / 10);
        if (codes[data.code] === 'humi') prev[currentSECOND].humiCount = 1;
        else prev[currentSECOND].tempCount = 1;
      }
      return prev;
    }, {});
    return { tempHumPerTimeData, firstTime };
  };

  const historyResolved = useMemo(() => {
    const MS_PER_SECOND = 1000;
    const { tempHumPerTimeData: historyMap, firstTime } =
      tempHumPerTime(MS_PER_SECOND);
    let prevTemp = '-';
    let prevHumi = '-';
    for (let time in historyMap) {
      if (historyMap[time].t === firstTime && !historyMap[time].temp) {
        historyMap[time].temp = prevTemp;
      } else {
        if (!historyMap[time].temp) {
          historyMap[time].temp = prevTemp;
        } else {
          let temp = Math.round(
            historyMap[time].temp / historyMap[time].tempCount
          );
          historyMap[time].temp = temp;
          prevTemp = temp;
        }
        if (!historyMap[time].humi) {
          historyMap[time].humi = prevHumi;
        } else {
          let hum = Math.round(
            historyMap[time].humi / historyMap[time].humiCount
          );
          historyMap[time].humi = hum;
          prevHumi = hum;
        }
      }
    }

    return Object.values(historyMap);
  }, [deviceCodeMap, deviceType, history]);

  const graphResolved = useMemo(() => {
    const MS_PER_HALF_MINUTES = 900 * 1000; // 30분
    const { tempHumPerTimeData: graphMap, firstTime } =
      tempHumPerTime(MS_PER_HALF_MINUTES);
    let prevTemp = '-';
    for (let time in graphMap) {
      if (time === firstTime && !graphMap[time].temp)
        graphMap[time].temp = prevTemp;
      else {
        if (!graphMap[time].temp) {
          graphMap[time].temp = prevTemp;
        } else {
          const temp = Math.round(
            graphMap[time].temp / graphMap[time].tempCount
          );
          graphMap[time].temp = temp;
          prevTemp = temp;
        }
        graphMap[time].humi = Math.round(
          graphMap[time].humi / graphMap[time].humiCount
        );
      }
    }
    return Object.values(graphMap);
  }, [deviceCodeMap, deviceType, history]);

  const historyListResolved = useMemo(() => {
    const historyMap = (historyResolved || []).reduceRight((prev, data) => {
      const date = moment(data.t).format('YYYY년 MM월 DD일');
      prev[date] = prev[date] || {
        date,
        list: []
      };
      prev[date]['list'].push(data);
      return prev;
    }, {});
    return Object.values(historyMap);
  }, [historyResolved]);

  const disabledDate = useCallback(current => {
    const startAt = moment().subtract(1, 'month').add(1, 'day').startOf('day');
    const endAt = moment().endOf('day');
    return !current.isBetween(startAt, endAt, 'day', '[]');
  }, []);

  const fetchHistory = useCallback(
    ({ deviceId, startAt, endAt }) => {
      const encodedStartAt = encodeURIComponent(moment(startAt).format());
      const encodedEndAt = encodeURIComponent(moment(endAt).format());

      const codes = Object.keys(deviceCodeMap[deviceType]).join(',');

      commonMethod.requestGetWithToken(
        `dashboard/devices/${deviceId}/report-logs?deviceType=ref-th&codes=${codes}&startAt=${encodedStartAt}&endAt=${encodedEndAt}&valueType=avg`,
        accessToken,
        { props: { history: routeHistory } },
        res => {
          if (!loading) dispatch({ type: 'setLoading', loading: false });
          dispatch({ type: 'setIsRefresh', isRefresh: false });
          dispatch({
            type: 'setHistory',
            history: res.result.list
          });
        },
        err => console.error(err)
      );
    },
    [deviceCodeMap, deviceType, dispatch, accessToken, routeHistory]
  );

  useEffect(() => {
    if (!deviceId || !startAt || !endAt) {
      return;
    }
    setVisualRange({ startValue: startAt, endValue: endAt });
    dispatch({ type: 'setLoading', loading: true });
    fetchHistory({ deviceId, startAt, endAt });
  }, [fetchHistory, deviceId, startAt, endAt, dispatch]);

  const onDateChange = useCallback(
    ([from, to]) => {
      dispatch({
        type: 'setDate',
        startAt: from.startOf('day').toDate(),
        endAt: to.endOf('day').toDate()
      });
    },
    [dispatch]
  );

  const onVisualRangeChange = useCallback(arg => {
    setVisualRange(arg);
  }, []);

  const customizeLabelText = useCallback(({ value, min, max }) => {
    return `${
      moment(max - min).diff(1, 'day') > 0
        ? moment(value).format('YY.MM.DD')
        : moment(value).format('YY.MM.DD HH:mm')
    }`;
  }, []);

  const customizeTooltip = useCallback(({ point }) => {
    return {
      html: `<div class="state-tooltip"><h4 class="tooltip-date">${moment(
        point.data.t
      ).format('YYYY.MM.DD HH:mm')}</h4><div class="tooltip-temp">온도 : ${
        point.data.temp ? point.data.temp : '-'
      }℃</div><div class="tooltip-humi">습도 : ${
        point.data.humi ? point.data.humi : '-'
      }%</div></div>`
    };
  }, []);

  const legendCustomizeText = useCallback(({ seriesIndex, seriesName }) => {
    return seriesIndex === 0 ? `${seriesName} (&#176C)` : `${seriesName} (%)`;
  }, []);

  const onZoomEnd = useCallback(e => {
    const formattedEndDate = moment(e.range.endValue);
    const formattedStartDate = moment(e.range.startValue);
    e.cancel =
      moment.duration(formattedEndDate.diff(formattedStartDate)).asHours() < 4;
  }, []);

  const onRefresh = useCallback(() => {
    dispatch({ type: 'setLoading', loading: true });
    fetchHistory({ deviceId, startAt, endAt });
  }, [deviceId, endAt, fetchHistory, startAt, dispatch]);

  useEffect(() => {
    isRefresh && fetchHistory({ deviceId, startAt, endAt });
  }, [isRefresh, fetchHistory, deviceId, startAt, endAt]);

  const onExport = useCallback(() => {
    const csvExporter = new ExportToCsv({
      fieldSeparator: ',',
      filename: deviceName,
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: true,
      title: `${deviceName} 히스토리`,
      useTextFile: false,
      useBom: true,
      headers: ['일시', '온도(℃)', '습도(%)']
    });

    const arr = historyResolved.map(item => ({
      date: moment(item.t).format('YYYY.MM.DD HH:mm'),
      temp: item.temp,
      humi: item.humi
    }));
    csvExporter.generateCsv(arr);
  }, [deviceName, historyResolved]);
  return (
    <div className="temp-humidity-history">
      <div className="date-range">
        <div className="date-range-device-name">
          <h3>{deviceName}</h3>
          <small>냉장고 온습도계</small>
        </div>
        <div className="temp-humi-date-range">
          Date :
          <RangePicker
            value={[moment(startAt), moment(endAt)]}
            onChange={onDateChange}
            disabledDate={disabledDate}
            format="YYYY.MM.DD"
          />
          <Popconfirm
            title={'export-refresh'}
            placement="bottomLeft"
            onConfirm={onExport}
            onCancel={onRefresh}
            okText="내보내기"
            cancelText="새로고침"
          >
            <Button>
              <svg
                width="4"
                height="16"
                viewBox="0 0 4 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 12C0.895431 12 4.82823e-08 12.8954 0 14C-4.82823e-08 15.1046 0.89543 16 2 16C3.10457 16 4 15.1046 4 14C4 12.8954 3.10457 12 2 12Z"
                  fill="#EAEAEA"
                />
                <path
                  d="M2 6C0.895431 6 3.10551e-07 6.89543 2.62268e-07 8C2.13986e-07 9.10457 0.895431 10 2 10C3.10457 10 4 9.10457 4 8C4 6.89543 3.10457 6 2 6Z"
                  fill="#EAEAEA"
                />
                <path
                  d="M5.24537e-07 2C5.72819e-07 0.89543 0.895431 -4.82823e-08 2 0C3.10457 4.82823e-08 4 0.895431 4 2C4 3.10457 3.10457 4 2 4C0.895431 4 4.76254e-07 3.10457 5.24537e-07 2Z"
                  fill="#EAEAEA"
                />
              </svg>
            </Button>
          </Popconfirm>
        </div>
      </div>

      <Tabs type="card" onChange={onRefresh}>
        <TabPane tab="그래프" key={1}>
          {loading && (
            <div className="flexible-refresh">
              <img src={Logo} alt="" />
            </div>
          )}
          {history && history.length === 0 && !loading && (
            <div className="no-temp-humidity-data">
              <svg
                width={24}
                height={24}
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M11.171 1.255a2 2 0 0 1 1.658 0c.41.186.67.517.85.782.178.263.368.61.579.993l8.334 15.154c.198.358.378.685.499.963.123.284.255.662.203 1.09a2 2 0 0 1-.82 1.388c-.35.252-.746.32-1.054.347-.302.028-.675.028-1.084.028H3.664c-.41 0-.783 0-1.084-.028-.308-.028-.703-.095-1.054-.347a2 2 0 0 1-.82-1.387c-.052-.429.08-.807.203-1.09.121-.279.301-.606.499-.964L9.742 3.03c.211-.384.401-.73.58-.993.179-.265.44-.596.85-.782Zm.808 1.902c-.12.178-.267.441-.505.874L3.18 19.11a11.98 11.98 0 0 0-.45.866l.032.004c.2.018.479.019.944.019h16.588a12.019 12.019 0 0 0 .976-.023 11.934 11.934 0 0 0-.45-.866L12.526 4.03A12.843 12.843 0 0 0 12 3.126l-.021.03ZM11 14V9h2v5h-2Zm0 4v-2h2v2h-2Z"
                  fill="#8A8B8D"
                />
              </svg>
              <p>현재 디바이스에 저장된 히스토리가 없습니다.</p>
            </div>
          )}
          {graphResolved.length > 0 && !loading && (
            <Chart
              ref={chart}
              id="chart"
              dataSource={graphResolved}
              style={{
                background: '#252629',
                borderRadius: '7px',
                padding: '1.5rem'
              }}
              onZoomEnd={onZoomEnd}
            >
              <Margin top={10} />
              <CommonSeriesSettings
                argumentField="t"
                type="spline"
                hoverMode="allArgumentPoints"
              >
                <Point
                  visible={true}
                  size={0}
                  hoverMode="allArgumentPoints"
                  hoverStyle={{ size: '5' }}
                />
              </CommonSeriesSettings>
              <Series
                color="#FAAF29"
                border={{ color: '#FAAF29', width: '1', visible: 'true' }}
                hoverStyle={{
                  border: {
                    color: '#FAAF29',
                    width: '2',
                    dashStyle: 'solid',
                    visible: 'true'
                  },
                  color: '#FAAF29',
                  hatching: { direction: 'none', opacity: 1 }
                }}
                name="온도"
                valueField="temp"
              />
              <Series
                border={{ color: '#4BB98C', width: '1', visible: 'true' }}
                color="#4BB98C"
                hoverStyle={{
                  border: {
                    color: '#4BB98C',
                    width: '2',
                    dashStyle: 'solid',
                    visible: 'true'
                  },
                  color: '#4BB98C',
                  hatching: { direction: 'none', opacity: 1 }
                }}
                name="습도"
                valueField="humi"
              />
              <ArgumentAxis
                visualRange={visualRange}
                onVisualRangeChange={onVisualRangeChange}
                allowDecimals={false}
                argumentType="datetime"
                axisDivisionFactor={100}
              >
                <Label customizeText={customizeLabelText} />
                <TickInterval minutes={1} />
              </ArgumentAxis>
              <Legend
                verticalAlignment="top"
                horizontalAlignment="left"
                itemTextPosition="right"
                itemsAlignment="left"
                customizeText={legendCustomizeText}
                rowCount="2"
              >
                <Margin top={0} left={0} />
              </Legend>
              <ScrollBar visible={false} />
              <ZoomAndPan argumentAxis="both" />
              <Export enabled={false} />
              <Crosshair
                enabled
                color="#8A8B8D"
                width={1}
                dashStyle="dash"
                horizontalLine={false}
              />
              <Tooltip
                cornerRadius={7}
                enabled={true}
                customizeTooltip={customizeTooltip}
                opacity={0.7}
                border={{ color: '#F4F4F4' }}
              />
              <ValueAxis
                autoBreaksEnabled={false}
                allowDecimals={false}
                axisDivisionFactor={1000000}
              >
                <VisualRange />
                <Label visible={true} />
              </ValueAxis>
            </Chart>
          )}
        </TabPane>
        <TabPane tab="리스트" key={2}>
          <div className="ref-history-list">
            {loading && (
              <div className="flexible-refresh motion-door">
                <img alt="loading.." src={Logo} />
              </div>
            )}
            {historyListResolved &&
              historyListResolved.length === 0 &&
              !loading && (
                <div className="no-motion-door-data">
                  <svg
                    width={24}
                    height={24}
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M11.171 1.255a2 2 0 0 1 1.658 0c.41.186.67.517.85.782.178.263.368.61.579.993l8.334 15.154c.198.358.378.685.499.963.123.284.255.662.203 1.09a2 2 0 0 1-.82 1.388c-.35.252-.746.32-1.054.347-.302.028-.675.028-1.084.028H3.664c-.41 0-.783 0-1.084-.028-.308-.028-.703-.095-1.054-.347a2 2 0 0 1-.82-1.387c-.052-.429.08-.807.203-1.09.121-.279.301-.606.499-.964L9.742 3.03c.211-.384.401-.73.58-.993.179-.265.44-.596.85-.782Zm.808 1.902c-.12.178-.267.441-.505.874L3.18 19.11a11.98 11.98 0 0 0-.45.866l.032.004c.2.018.479.019.944.019h16.588a12.019 12.019 0 0 0 .976-.023 11.934 11.934 0 0 0-.45-.866L12.526 4.03A12.843 12.843 0 0 0 12 3.126l-.021.03ZM11 14V9h2v5h-2Zm0 4v-2h2v2h-2Z"
                      fill="#8A8B8D"
                    />
                  </svg>
                  <p>현재 디바이스에 저장된 히스토리가 없습니다.</p>
                </div>
              )}
            {historyListResolved.length > 0 &&
              historyListResolved.map(({ date, list }, index) => {
                return (
                  <ul className="ref-history" key={index}>
                    <h4>{date}</h4>
                    {list.length > 0 &&
                      list.map((data, index) => {
                        return (
                          <li key={index}>
                            <p className="ref-history-time">
                              {moment(data.t).format('hh : mm : ss')}
                            </p>
                            <p className="ref-history-temp">{data.temp}℃</p>
                            <p className="ref-history-humi">{data.humi}%</p>
                          </li>
                        );
                      })}
                  </ul>
                );
              })}
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
}

export default React.memo(RefTempHumidityHistory);
